/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TSimpleEventEmitter } from "@adapptio/lib-shared";
import React, { useState } from "react";
import { useLatest } from "./useLatest";
import { useSimpleEvent } from "./useSimpleEvent";

/**
 * Calls the getter function to resolve a value. Then the value is stored in the state and event listener is bound.
 * When event is emitted the new value is computed via getter again and stored in the state.
 *
 * @param eventEmitter Simple event emitter
 * @param getter Function to get value
 * @param deps React dependency list
 * @param active If event should be bound
 */
export function useStateWithEvent<
	TEmitter extends TSimpleEventEmitter<unknown> = TSimpleEventEmitter<unknown>,
	TValue = unknown
>(
	eventEmitter: TEmitter,
	getter: (setState: (state: TValue) => void) => TValue,
	deps: React.DependencyList = [],
	active = true
): TValue {
	const getterRef = useLatest(getter);
	const [ state, setState ] = useState(() => {
		let finalState;
		let setStateCalled = false;
		const setStateMock = (value) => {
			finalState = value;
			setStateCalled = true;
		};

		const val = getter(setStateMock);

		if (setStateCalled) {
			return finalState;
		}

		return val;
	});

	useSimpleEvent(
		eventEmitter,
		() => {
			let setStateCalled = false;

			const setStateCb = (value) => {
				setStateCalled = true;
				setState(value);
			};

			const value = getterRef.current(setStateCb);

			if (!setStateCalled) {
				setState(value);
			}
		},
		deps,
		active
	);

	return state;
}
